import type { AgentHost, DeviceProperties, StorageInfo } from '@runtime/agent.js';

import { collectDeviceProperties, getStorageInfo } from './device-properties.js';
import { isHttpUrl } from './url-safety.js';

/**
 * AgentHost backed by a WebView `@JavascriptInterface` bridge exposed
 * by the native Android kiosk shell (packages/lockdown-android).
 *
 * The bridge is registered as `window.MobiLauncherBridge` by the
 * Android side. Methods are synchronous on the bridge (Java's
 * JavascriptInterface contract) and return a string: `"ok"` on success,
 * or `"error: <message>"` when the bridge can't fulfill the request
 * (package not installed, no admin rights, etc.). This host wraps each
 * call in a Promise that resolves/rejects accordingly so the engine's
 * normal error handling kicks in unchanged.
 *
 * This host backs the DEPLOYMENT BACKBONE: the native Android shell running
 * as a HOME app under lock task, provisioned by Android Enterprise / AMAPI
 * managed configuration (see packages/lockdown-android). It's the correct-
 * engineering path for dedicated-device fleets and needs no third-party MDM.
 *
 * SOTI's JS Agent API (SotiAgentHost) is one adapter ALONGSIDE this, selected
 * only for SOTI Lockdown deployments — it powers the agent-action tiles there.
 * Neither is "the foundation": they're peer adapters behind the same AgentHost
 * seam, and the launcher picks whichever native bridge is actually present.
 */
export class AndroidIntentAgentHost implements AgentHost {
  constructor(private readonly bridge: AndroidBridge = requireBridge()) {}

  launchApp(packageName: string): Promise<void> {
    return resultToPromise(this.bridge.launchApp(packageName));
  }

  openUrl(url: string, options?: { external?: boolean; tab?: 'same' | 'new' }): Promise<void> {
    // Defense in depth: don't hand a javascript:/data:/intent: URL to the
    // native bridge (which would navigate the WebView or fire an intent).
    if (!isHttpUrl(url)) {
      return Promise.reject(new Error(`openUrl refused non-http(s) URL: ${url}`));
    }
    const external = options?.external ?? false;
    const tab = options?.tab ?? 'new';
    // Prefer the three-arg form (adds Chrome tab control). But Android's
    // WebView JS bridge resolves @JavascriptInterface methods by ARGUMENT
    // COUNT — an older shell that only declares openUrl(url, external)
    // has no 3-arg overload, so the call throws "method not found" (or
    // returns a non-envelope value) rather than ignoring the extra arg.
    // Fall back to the two-arg form so external opens keep working; tab
    // control just degrades to the shell's default until the APK updates.
    let result: unknown;
    try {
      result = this.bridge.openUrl(url, external, tab);
    } catch {
      result = undefined;
    }
    if (!isBridgeEnvelope(result)) {
      result = this.bridge.openUrl(url, external);
    }
    return resultToPromise(result);
  }

  reboot(): Promise<void> {
    return resultToPromise(this.bridge.reboot());
  }

  clearAppData(packageName: string | undefined): Promise<void> {
    // Bridge expects an empty string for "the launcher itself" — the
    // JS interface can't carry undefined cleanly across the bridge.
    return resultToPromise(this.bridge.clearAppData(packageName ?? ''));
  }

  runScript(scriptRef: string): Promise<void> {
    return resultToPromise(this.bridge.runScript(scriptRef));
  }

  syncNow(): Promise<void> {
    return resultToPromise(this.bridge.syncNow());
  }

  /**
   * Declare the set of packages the kiosk allows to run alongside it
   * inside lock-task mode. Used by App.tsx after config load — extract
   * every `type: 'app'` tile's packageName and push them all to native.
   *
   * On non-device-owner installs (the common case) the bridge no-ops
   * this and returns "ok" — lock-task isn't engaged so there's nothing
   * to allowlist. On device-owner-enrolled installs, the bridge
   * forwards to DPM.setLockTaskPackages so launching Gmail / Chrome /
   * etc. doesn't drop pinning.
   *
   * The bridge always includes the kiosk's own package on the list.
   * If the bridge predates this method (older APK), this resolves as
   * a no-op rather than rejecting — the renderer should never hard-
   * fail because of an old shell.
   */
  setAllowedPackages(packages: readonly string[]): Promise<void> {
    if (typeof this.bridge.setAllowedPackages !== 'function') {
      return Promise.resolve();
    }
    return resultToPromise(this.bridge.setAllowedPackages(JSON.stringify(packages)));
  }

  /**
   * Best-of-both: layer MDM-style identity from the bridge over the
   * browser-derived UA/screen/network info. The bridge supplies
   * `deviceName`, `serialNumber`, etc.; the WebView fills in the rest.
   */
  async getDeviceProperties(): Promise<DeviceProperties> {
    const browser = collectDeviceProperties();
    const storage = await getStorageInfo();
    const out: DeviceProperties = {
      formFactor: browser.formFactor,
      os: browser.os,
      browser: browser.browser,
      language: browser.language,
      timezone: browser.timezone,
      online: browser.online,
      screen: browser.screen,
    };
    if (browser.hardwareConcurrency !== undefined) {
      out.hardwareConcurrency = browser.hardwareConcurrency;
    }
    if (browser.deviceMemoryGb !== undefined) out.deviceMemoryGb = browser.deviceMemoryGb;
    if (storage) {
      out.storageUsageBytes = storage.usageBytes;
      out.storageQuotaBytes = storage.quotaBytes;
    }
    if (browser.connection) out.connection = browser.connection;

    // Bridge-supplied fields — optional; only set when the bridge knows.
    const propsJson = this.bridge.getDeviceProperties?.();
    if (typeof propsJson === 'string' && propsJson.length > 0) {
      try {
        const parsed = JSON.parse(propsJson) as Partial<DeviceProperties>;
        if (typeof parsed.deviceName === 'string') out.deviceName = parsed.deviceName;
        if (typeof parsed.serialNumber === 'string') out.serialNumber = parsed.serialNumber;
        if (typeof parsed.manufacturer === 'string') out.manufacturer = parsed.manufacturer;
        if (typeof parsed.model === 'string') out.model = parsed.model;
        // Battery — native only (the browser Battery API is gone from Chrome).
        if (typeof parsed.batteryLevel === 'number') out.batteryLevel = parsed.batteryLevel;
        if (typeof parsed.batteryCharging === 'boolean') out.batteryCharging = parsed.batteryCharging;
      } catch {
        // Malformed bridge response — ignore; the browser-derived fields
        // are still useful on their own.
      }
    }
    return out;
  }

  /**
   * Real device storage + RAM from the native bridge, mapped to StorageInfo.
   * The browser's navigator.storage.estimate() (used by getDeviceProperties)
   * only sees the WebView's own quota; this reports the actual device volume
   * and system memory. Returns an empty StorageInfo when the bridge is an older
   * APK without the method or a probe failed.
   */
  getStorageInfo(): Promise<StorageInfo> {
    const json = this.bridge.getStorageInfo?.();
    if (typeof json !== 'string' || json.length === 0) return Promise.resolve({});
    try {
      const parsed = JSON.parse(json) as StorageInfo;
      const out: StorageInfo = {};
      if (parsed.internal && typeof parsed.internal.totalSpace === 'number') {
        out.internal = {
          totalSpace: parsed.internal.totalSpace,
          ...(typeof parsed.internal.availableSpace === 'number'
            ? { availableSpace: parsed.internal.availableSpace }
            : {}),
        };
      }
      if (parsed.memory && typeof parsed.memory.totalSpace === 'number') {
        out.memory = {
          totalSpace: parsed.memory.totalSpace,
          ...(typeof parsed.memory.availableSpace === 'number'
            ? { availableSpace: parsed.memory.availableSpace }
            : {}),
        };
      }
      return Promise.resolve(out);
    } catch {
      return Promise.resolve({});
    }
  }
}

/* ───── Bridge contract ───── */

/**
 * Shape of the `window.MobiLauncherBridge` object the Android kiosk
 * shell registers via WebView.addJavascriptInterface. All methods
 * return a string — `"ok"` on success or `"error: <reason>"` on failure.
 *
 * The `@JavascriptInterface`-annotated Kotlin methods can only return
 * primitives, hence the string-encoded result envelope.
 */
export interface AndroidBridge {
  launchApp(packageName: string): string;
  /**
   * `external` is passed positionally — the bridge uses Intent.ACTION_VIEW
   * when true. `tab` ('new' | 'same') is an optional third arg controlling
   * Chrome tab reuse on the external path; older bridges that declare only
   * two params ignore it and behave as 'new'.
   */
  openUrl(url: string, external: boolean, tab?: string): string;
  reboot(): string;
  clearAppData(packageName: string): string;
  runScript(scriptRef: string): string;
  syncNow(): string;
  /** Optional — returns a JSON-stringified Partial<DeviceProperties>. */
  getDeviceProperties?(): string;
  /** Optional (older APKs don't ship it) — returns a JSON-stringified
   *  StorageInfo ({ internal, memory } with byte totals/available). */
  getStorageInfo?(): string;
  /**
   * Optional (older APKs don't ship it) — declare the allowlist for
   * lock-task mode. Input is a JSON-stringified array of packageName
   * strings. On non-device-owner devices the call is a silent no-op.
   */
  setAllowedPackages?(packagesJson: string): string;
}

declare global {
  interface Window {
    MobiLauncherBridge?: AndroidBridge;
  }
}

/**
 * Probe for the bridge. Returns true when the JS interface is present —
 * the boot path calls this to decide between AndroidIntentAgentHost
 * and BrowserAgentHost.
 */
export function hasAndroidBridge(): boolean {
  return typeof window !== 'undefined' && typeof window.MobiLauncherBridge !== 'undefined';
}

function requireBridge(): AndroidBridge {
  if (typeof window === 'undefined' || !window.MobiLauncherBridge) {
    throw new Error(
      'AndroidIntentAgentHost requires window.MobiLauncherBridge — running outside the Android kiosk shell?',
    );
  }
  return window.MobiLauncherBridge;
}

/**
 * True when `result` is a well-formed bridge envelope (`"ok"` or
 * `"error: …"`). Used to detect when a 3-arg `openUrl` call didn't
 * resolve to a real method on an older shell (threw → undefined, or
 * returned garbage) so the caller can retry the 2-arg form.
 */
function isBridgeEnvelope(result: unknown): boolean {
  return result === 'ok' || (typeof result === 'string' && result.startsWith('error:'));
}

/**
 * Bridge result envelope decoder. Successful calls return `"ok"`;
 * failures return `"error: <message>"`. Anything else is treated as
 * a malformed bridge response.
 */
function resultToPromise(result: unknown): Promise<void> {
  if (result === 'ok') return Promise.resolve();
  if (typeof result === 'string' && result.startsWith('error:')) {
    return Promise.reject(new Error(result.slice('error:'.length).trim()));
  }
  return Promise.reject(
    new Error(`unexpected bridge result: ${typeof result === 'string' ? result : String(result)}`),
  );
}
